import { Component, input, output } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { AlertController, ActionSheetController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { translateValue } from "src/app/helpers/translate/translateHelper";
import { InspectionTypeItemsResponse, ItemTypeCaptionsDetails } from "src/app/models/inspection.model";
import { CoreService } from "src/app/services/core/core.service";
import { environment } from "src/environments/environment";
import { CameraSource } from '@capacitor/camera';
import { PhotoUploadService } from 'src/app/services/photo-upload/photo-upload.service';
import { MediaUploadDetails } from "src/app/models/media.model";


/**
 * Platform-specific maximum file sizes (converted to bytes).
 * These are intentionally resolved at build-time from environment
 * to ensure consistency across web/mobile deployments.
 */
const WEB_FILE_SIZE = Number(environment.webAppFileSize) * 1024 * 1024;
const IOS_FILE_SIZE = Number(environment.iosFileSize) * 1024 * 1024;
const ANDROID_FILE_SIZE = Number(environment.androidFileSize) * 1024 * 1024;

@Component({
  selector: 'app-pui-templates',
  templateUrl: './pui-templates.component.html',
  styleUrls: ['./pui-templates.component.scss'],
  standalone: false
})
export class PUITemplatesComponent {

  /**
   * Signal-based inputs are used intentionally to support
   * reactive updates without manual change detection.
   */
  inspectionItems = input<InspectionTypeItemsResponse[]>([]);
  errorPayload = input<string[]>([]);
  showTowingQn = input.required<boolean>();

  /**
   * Caption configuration received from parent.
   * Used for dynamic validation messaging.
   */
  buttonCaptions = input<ItemTypeCaptionsDetails>();
  isEditInspection = input<boolean>();

  /** Non-fleet (hire vehicle) inspections, Cancel should return to the dashboard */
  isNonFleetInspection = input<boolean>(false);

  /**
   * UI state flags – intentionally kept local to prevent
   * parent-driven side effects during validation flows.
   */
  tempFormSubmitted: boolean = false;
  isRoadoworthySubmitted: boolean = false;
  isTowingTrailerSubmitted: boolean = false;
  isFormSubmitted: boolean = false;
  isValidMimetype: boolean = false;
  isFileSizeValid: boolean = false;

  imageData: MediaUploadDetails | null = null;
  imageObj: any;
  invalidFileMessage: string = '';

  /**
   * Flags used to drive inline / top-level validation visibility.
   * These are separated to avoid coupling photo & comment errors.
   */
  showPhotoMandatoryAlwaysError: boolean = false;
  showPhotoMandatorySafetyError: boolean = false;
  showImage: boolean = false;

  /**
   * Emits form submission intent only.
   * Validation is handled internally before emitting.
   */
  emitSubmission = output<void>();

  /**
   * Roadworthy is tri-state:
   * - true  → explicitly roadworthy
   * - false → safety defect exists
   * - undefined → not yet decided
   */
  isRoadWorthy: boolean | undefined = undefined;

  /**
   * TowingTrailer is tri-state:
   * - true  → explicitly roadworthy
   * - false → safety defect exists
   * - undefined → not yet decided
   */
  isTowingTrailer: boolean | undefined = undefined;

  /**
   * Local working list used for template-level validation scans.
   * This is intentionally decoupled from inspectionItems()
   * to avoid mutating parent state during iteration.
   */
  itemListForTemplate: InspectionTypeItemsResponse[] = [];

  topCommentError: string = '';

  /**
   * Emits top-level validation keys to parent
   * so parent can remove resolved errors.
   */
  removeValidation = output<string[]>();

  constructor(
    private readonly translateService: TranslateService,
    private readonly coreService: CoreService,
    private readonly route: ActivatedRoute,
    private readonly alertCtrl: AlertController,
    private readonly actionSheetCtrl: ActionSheetController,
    private readonly photoUploadService: PhotoUploadService
  ) { }

  /**
   * Returns section headers only.
   * Computed dynamically to support real-time updates.
   */
  get sectionHeader() {
    return this.inspectionItems()
      .filter(item => item.iS_SECTION_HEADER !== null && item.iS_SECTION_HEADER);
  }

  get isRoadworthyValidation() {
    return this.isRoadoworthySubmitted && this.isRoadWorthy == undefined;
  }

  get isTrailerValidation() {
    return this.isTowingTrailerSubmitted && this.showTowingQn() && this.isTowingTrailer == undefined;
  }

  /** Hire vehicle inspections have no edit/show flow, so Cancel goes to the dashboard instead of the inspection list */
  getCancelRedirectUrl(): string {
    return this.isNonFleetInspection() ? '/dashboard' : '/pre-use-inspection';
  }

  getInputValidations() {
    const validationList: string[] = [];

    const inspectionItems = this.inspectionItems().filter(x => ['TEXT', 'POSTCODE', 'NUMBER'].includes(x.inpuT_TYPE!));
    inspectionItems.forEach(x => {
      if (!x.item_Value) {
        const validation = `Please enter ${x.iteM_DESCRIPTION}`;
        x.commentError = validation;
        validationList.push(validation);
      }
    });

    return validationList;

    // itemTypesToValidate.forEach(({ type, condition }) => {
    //   let items: InspectionTypeItemsResponse[] = this.inspectionItems().filter(item => item.inpuT_TYPE === type);
    //   let filledItems = items.filter(item => condition(item.item_Value));

    //   if (items.length !== filledItems.length && this.insertItem.reportId === 0) {
    //     items.forEach(item => {
    //       this.globalValidations.push("Please enter " + item.iteM_DESCRIPTION);
    //     });
    //     this.fillValue = true;
    //   }
    // });
  }

  itemValueChange(item: InspectionTypeItemsResponse) {
    this.removeValidation.emit([item?.commentError ?? '']);
    item.commentError = '';
  }

  /**
   * Returns items belonging to a specific section header.
   */
  getsectionItems(header: InspectionTypeItemsResponse) {
    return this.inspectionItems()
      .filter(item => item.sectioN_ITEM_ID === header.veH_INSPECT_ITEM_ID);
  }

  /**
   * Handles toggling item back to neutral state.
   * Guard clause prevents redundant resets.
   */
  async toggleSelect(itemType: InspectionTypeItemsResponse) {
    if (itemType.isDefet != null && itemType.isDefet === false && itemType.iS_NA == false) {
      return;
    }

    /**
     * Reset all dependent properties to avoid stale validation states.
     */
    itemType.isDefet = false;
    itemType.iS_NA = false;
    itemType.safetY_IMPACT_IND = 'N';
    itemType.safetY_IMPACT_IND_BOOL = false;
    itemType.comment = null;
    itemType.defect_Imedia = null;
    itemType.defect_Imedia_Mime_Type = null;
    itemType.photO_MANDATORY_ANS = false;

    /**
     * Re-evaluate roadworthy status globally.
     */
    const inspectionItems = this.inspectionItems()
      .filter(item => item.safetY_IMPACT_IND_BOOL == true);
    this.isRoadWorthy = inspectionItems.length > 0 ? false : undefined;

    this.removeValidations();
  }

  /**
   * Performs all photo & comment validation checks
   * and returns unique top-level validation messages.
   */
  validatePhotoErrors() {
    let validationErrors = new Set<string>();

    for (const sa of this.itemListForTemplate) {
      if (sa.inpuT_TYPE !== 'TICKBOX') continue;

      /**
       * PHOTO mandatory when defect is selected (ALWAYS).
       */
      if (
        sa.photO_MANDATORY === 'ALWAYS' &&
        sa.isDefet === true &&
        !sa.defect_Imedia
      ) {
        sa.photO_MANDATORY_ANS = true;
        validationErrors.add(this.translateStr('PHOTO_VALIDATION_TOP1'));
      }

      /**
       * PHOTO mandatory when SAFETY defect impacts roadworthiness.
       */
      if (
        sa.photO_MANDATORY == 'SAFETY' &&
        this.isRoadWorthy == false &&
        sa.safetY_IMPACT_IND == 'Y' &&
        sa.safetY_IMPACT_IND_BOOL == true &&
        !sa.defect_Imedia
      ) {
        validationErrors.add(this.translateStr('PHOTO_VALIDATION_TOP1'));
        sa.photO_MANDATORY_ANS = true;
      }

      /**
       * COMMENT mandatory when SAFETY defect is marked.
       */
      if (
        sa.commenT_MANDATORY == 'SAFETY' &&
        sa.safetY_IMPACT_IND == 'Y' &&
        sa.safetY_IMPACT_IND_BOOL == true &&
        (!sa.comment || sa.comment.trim() == '')
      ) {
        sa.commentError = this.translateStr('COMMENT_VALIDATION_INLINE1');
        validationErrors.add(this.translateStr('COMMENT_VALIDATION_TOP1'));
      }

      /**
       * COMMENT mandatory when ALWAYS required.
       */
      if (
        sa.commenT_MANDATORY === 'ALWAYS' &&
        sa.isDefet == true &&
        (!sa.comment || sa.comment.trim() === '')
      ) {
        sa.commentError = this.translateStr('COMMENT_VALIDATION_INLINE1');
        validationErrors.add(this.translateStr('COMMENT_VALIDATION_TOP1'));
      }

      ///Top level type validation for mandatory photo upload on web
      if ((sa.photO_MANDATORY == 'ALWAYS' || (sa.photO_MANDATORY == 'SAFETY' && sa.safetY_IMPACT_IND == 'Y' && sa.safetY_IMPACT_IND_BOOL == true)) && !sa.isFileTypeValid && sa.invalidFileMessage) {
        this.invalidFileMessage = this.translateService.instant('UPLOAD_PUI_VAIDATION');
        validationErrors.add(sa.invalidFileMessage);
      }

      ///Top level max size validation for mandatory photo upload on web
      const { sizeLabel } = this.photoUploadService.getMaxAllowedImageSize();
      if ((sa.photO_MANDATORY == 'ALWAYS' || (sa.photO_MANDATORY == 'SAFETY' && sa.safetY_IMPACT_IND == 'Y' && sa.safetY_IMPACT_IND_BOOL == true)) && !sa.isFileSizeValid && sa.invalidFileMessage) {
        this.invalidFileMessage = this.translateService.instant('MSG_FILE_UPLOAD_SIZE_VALIDATION', { size: sizeLabel });
        validationErrors.add(sa.invalidFileMessage);
      }
    }

    return validationErrors;
  }

  /**
   * Validates all comments including business rules
   * and content quality rules.
   */
  validateAllComments() {
    let validationList = new Set<string>();

    for (const sa of this.itemListForTemplate) {
      if (sa.inpuT_TYPE !== 'TICKBOX' || !sa.isDefet) continue;

      const isMandatory =
        sa.commenT_MANDATORY === 'ALWAYS' ||
        (sa.commenT_MANDATORY === 'SAFETY' && sa.safetY_IMPACT_IND_BOOL);

      sa.commentError = null;

      /**
       * Mandatory empty comment validation.
       */
      if (!sa.comment || sa.comment.trim() === '') {
        if (isMandatory) {
          sa.commentError = this.translateStr('COMMENT_VALIDATION_INLINE1');
          validationList.add(this.translateStr('COMMENT_VALIDATION_TOP1'));
        }
        continue;
      } else {
        /**
         * Content quality validation.
         */
        const isValid = this.validateComment(
          sa,
          { detail: { value: sa.comment } }
        );

        if (!isValid) {
          validationList.add(this.translateStr('COMMON_COMMENT_TOP'));
        }
      }
    }

    return validationList;
  }

  /**
   * Marks an item as failed (defect).
   * Guard clause prevents duplicate toggles.
   */
  toggleFail(item: InspectionTypeItemsResponse) {
    if (item.isDefet != null && item.isDefet === true && !item.iS_NA) {
      return;
    }

    item.isDefet = true;
    item.iS_NA = false;
    item.commentError = null;
    item.defect_Imedia = null;
    item.defect_Imedia_Mime_Type = null;

    /**
     * Maintain a working list for validation iteration.
     */
    this.itemListForTemplate.push(item);
    const inspectionItems = this.inspectionItems()
      .filter(item => item.safetY_IMPACT_IND_BOOL == true);
    this.isRoadWorthy = inspectionItems.length > 0 ? false : undefined;

    this.removeValidations();
  }

  /**
   * Marks an item as Not Applicable.
   * Clears all dependent defect & safety states.
   */
  toggleNa(item: InspectionTypeItemsResponse) {
    if (item.iS_NA != null && item.iS_NA) {
      return;
    }

    if (item.iS_NA == null || !item.iS_NA) {
      item.iS_NA = true;
      item.isDefet = false;
      item.safetY_IMPACT_IND = 'N';
      item.safetY_IMPACT_IND_BOOL = false;
      item.comment = null;
      item.defect_Imedia = null;
      item.defect_Imedia_Mime_Type = null;
      item.photO_MANDATORY_ANS = false;

      /**
       * Re-evaluate roadworthiness after NA selection.
       */
      const inspectionItems = this.inspectionItems()
        .filter(item => item.safetY_IMPACT_IND_BOOL == true);
      this.isRoadWorthy = inspectionItems.length > 0 ? false : undefined;
    } else {
      item.iS_NA = false;
    }

    this.removeValidations();
  }

  /**
   * Maps backend input types to HTML input types.
   */
  getInpType(inputType: string) {
    return inputType == 'NUMBER' ? 'number' : 'text';
  }

  /**
   * Handles Safety Impact toggle.
   */
  onChangeSafetyToggle(itemType: InspectionTypeItemsResponse): void {
    this.isFormSubmitted = false;
    this.isTowingTrailerSubmitted = false;
    this.isRoadoworthySubmitted = false;

    const isSafetyOn = itemType.safetY_IMPACT_IND_BOOL;
    this.isTowingTrailer = undefined;

    this.removeValidation.emit([translateValue('ROADWORTHY_MSG', this.translateService)]);
    this.removeValidation.emit([translateValue('TOWING_TRAILER_ERROR', this.translateService)]);
    if (!isSafetyOn) {

      itemType.safetY_IMPACT_IND = null;
      itemType.photO_MANDATORY_ANS = itemType.photO_MANDATORY !== 'SAFETY' && this.isFormSubmitted;

      if (itemType.commenT_MANDATORY === 'SAFETY' && this.isFormSubmitted) {
        itemType.commentError = null;
      }

      /**
       * If no safety defects exist, roadworthiness becomes undecided.
       */
      const count = this.inspectionItems().reduce(
        (acc, item) => acc + (item.safetY_IMPACT_IND_BOOL === true ? 1 : 0),
        0
      );

      if (count == 0) {
        this.isRoadWorthy = undefined;
      }

      return;
    }

    /**
     * Safety defect forces roadworthiness to false.
     */
    itemType.safetY_IMPACT_IND = 'Y';
    itemType.safetY_IMPACT_IND_BOOL = true;
    this.isRoadWorthy = false;
  }

  /**
   * Indicates if at least one safety defect exists.
   */
  get isDefectSafetyRelated() {
    return this.inspectionItems()
      .filter(item => item.safetY_IMPACT_IND_BOOL == true).length > 0;
  }

  /**
   * Validates comment content based on business rules.
   * Also normalizes comment value.
   */
  validateComment(item: InspectionTypeItemsResponse, ev: any): boolean {
    const rawValue = ev.detail.value || '';

    item.commentError = null;
    item.counter = rawValue.length;

    /**
     * Normalize spaces and trim.
     */
    const value = rawValue.replaceAll(/\s+/g, ' ').trim();
    item.comment = value;

    let isValid = true;

    /**
     * Content quality validations.
     */
    if (rawValue.length > 0 && !rawValue.trim()) {
      item.commentError = this.translateStr('REPEATED_COMMENT');
      isValid = false;
    } else if (this.tempFormSubmitted && this.checkItemCommentValidation(item) && !rawValue.trim()) {
      item.commentError = this.translateStr('COMMENT_VALIDATION_INLINE1');
      isValid = false;
    }
    else if (!value) {
      isValid = true;
    } else if (this.containsEmoji(value)) {
      item.commentError = this.translateStr('EMOJI_COMMENT');
      isValid = false;
    } else if (value.length < 10) {
      item.commentError = this.translateStr('MANIMUM_COMMENT_INLINE');
      isValid = false;
    } else if (!this.hasEnoughDistinctCharacters(value)) {
      item.commentError = this.translateStr('REPEATED_COMMENT');
      isValid = false;
    } else if (value.length > 500) {
      item.commentError = this.translateStr('MAXIMUM_COMMENT_INLINE');
      isValid = false;
    }

    return isValid;
  }

  checkItemCommentValidation(item: InspectionTypeItemsResponse) {
    return (
      item.commenT_MANDATORY === 'ALWAYS' ||
      (
        item.commenT_MANDATORY === 'SAFETY' &&
        item.safetY_IMPACT_IND_BOOL
      )
    )
  }

  /**
   * Centralized cleanup of resolved validations.
   * Emits only when a validation category is fully cleared.
   */
  removeValidations() {
    // COMMENT mandatory validation removal
    const reqdCommentValidCount = this.inspectionItems().reduce(
      (acc, item) => acc + (
        (
          item.commenT_MANDATORY == 'ALWAYS' &&
          item.commentError == this.translateStr('COMMENT_VALIDATION_INLINE1') &&
          item.isDefet == true
        ) ||
          (
            item.commenT_MANDATORY == 'SAFETY' &&
            item.safetY_IMPACT_IND_BOOL == true &&
            item.commentError == this.translateStr('COMMENT_VALIDATION_INLINE1') &&
            item.isDefet == true
          )
          ? 1 : 0
      ),
      0
    );

    if (reqdCommentValidCount == 0) {
      this.removeValidation.emit([this.translateStr('COMMENT_VALIDATION_TOP1')]);
    }

    // COMMENT quality validation removal
    const count = this.inspectionItems().reduce(
      (acc, item) => acc + (
        (
          item.commenT_MANDATORY == 'ALWAYS' &&
          item.commentError &&
          ![this.translateStr('COMMENT_VALIDATION_INLINE1')].includes(item.commentError) &&
          item.isDefet == true
        ) ||
          (
            item.commenT_MANDATORY == 'SAFETY' &&
            item.commentError &&
            item.safetY_IMPACT_IND_BOOL == true &&
            ![this.translateStr('COMMENT_VALIDATION_INLINE1')].includes(item.commentError) &&
            item.isDefet == true
          )
          ? 1 : 0
      ),
      0
    );

    if (count == 0) {
      this.removeValidation.emit([this.translateStr('COMMON_COMMENT_TOP')]);
    }

    // PHOTO validation removal
    const photoValidationsCount = this.inspectionItems().reduce(
      (acc, item) => acc + (
        (item.photO_MANDATORY == 'ALWAYS' && item.isDefet === true && item.defect_Imedia == null) ||
          (
            item.photO_MANDATORY == 'SAFETY' &&
            this.isRoadWorthy == false &&
            item.safetY_IMPACT_IND == null &&
            item.safetY_IMPACT_IND_BOOL == false &&
            !item.defect_Imedia
          )
          ? 1 : 0
      ),
      0
    );

    if (photoValidationsCount == 0) {
      this.removeValidation.emit([this.translateStr('PHOTO_VALIDATION_TOP1')]);
    }

    // TICKBOX validation removal
    const tickBoxValidationsCount = this.inspectionItems().reduce(
      (acc, item) => acc + (
        !item.iS_SECTION_HEADER &&
          (
            (!item.alloW_NA && item.isDefet == null) ||
            (item.alloW_NA && (item.isDefet == null || item.iS_NA == null))
          )
          ? 1 : 0
      ),
      0
    );

    if (tickBoxValidationsCount == 0) {
      this.removeValidation.emit([
        `You must select ${this.buttonCaptions()?.tickboX_Y_CAPTION} or ${this.buttonCaptions()?.tickboX_N_CAPTION} for every inspection item.`
      ]);
    }
  }

  /**
   * Detects emoji usage – restricted by business rules.
   */
  private containsEmoji(value: string): boolean {
    return /\p{Extended_Pictographic}/u.test(value);
  }

  /**
   * Prevents repeated character spam (e.g. "aaaa").
   */
  private hasEnoughDistinctCharacters(value: string): boolean {
    return !/(.)\1{3,}/.test(value);
  }

  /**
   * Centralized translation helper.
   */
  translateStr(value: string) {
    return translateValue(value, this.translateService);
  }

  /**
   * Indicates whether the app is running inside mobile container.
   */
  get isMobileApp() {
    return this.coreService.isMobileApp;
  }

  /**
   * Handles media upload with:
   * - MIME validation
   * - Platform size validation
   */
  async fileUpload(event: any, id: number): Promise<void> {

    const inspectionItem = this.inspectionItems().find(item => item.veH_INSPECT_ITEM_ID === id);
    if (!inspectionItem) return;

    this.isValidMimetype = false;

    /**
     * Explicit whitelist of allowed media types.
     */
    const allowedMimeTypes = new Set(['image/jpeg', 'image/jpg', 'image/png']);

    const file: File | undefined =
      event?.type === 'change'
        ? event?.target?.files?.[0]
        : event?.target?.control?.files?.[0];

    if (!file) return;
    /**
     * Reject invalid or unsupported files early.
     */
    if (!allowedMimeTypes.has(file.type)) {
      this.isValidMimetype = true;
      inspectionItem.isFileTypeValid = false;
      inspectionItem.invalidFileMessage = this.translateService.instant('UPLOAD_PUI_VAIDATION');
      this.imageData = null;
      inspectionItem.defect_Imedia = null;
      inspectionItem.defect_Imedia_Mime_Type = null;
      return;
    }

    /**
     * Enforce platform-specific size limits.
     */
    const { maxAllowedSize, sizeLabel } = this.photoUploadService.getMaxAllowedImageSize();
    if (file.size != null && file.size > maxAllowedSize) {
      inspectionItem.isFileSizeValid = false;
      inspectionItem.invalidFileMessage = this.translateService.instant('MSG_FILE_UPLOAD_SIZE_VALIDATION', { size: sizeLabel });
      this.imageData = null;
      inspectionItem.defect_Imedia = null;
      inspectionItem.defect_Imedia_Mime_Type = null;
      return;
    }

    const compressedFile = await this.photoUploadService.compressImage(file, "defect");
    const base64Data = await this.photoUploadService.toBase64(compressedFile);

    this.imageData = {
      data: { base64Data },
      mimeType: compressedFile.type,
      name: compressedFile.name,
      description: 'Asset image for inspection',
      size: compressedFile.size
    };

    inspectionItem.isFileTypeValid = true;
    inspectionItem.isFileSizeValid = true;
    inspectionItem.invalidFileMessage = null;

    /**
     * Attach media to the corresponding inspection item.
     */
    inspectionItem.defect_Imedia = base64Data;
    inspectionItem.defect_Imedia_Mime_Type = this.imageData.mimeType;
    inspectionItem.photO_MANDATORY_ANS = false;

    this.resetUploadState(id);
    event.target.value = '';
  }

  /**
   * Resolves max file size based on platform.
   */
  private getPlatformFileSize() {
    if (!this.isMobileApp) {
      return {
        maxAllowedSize: WEB_FILE_SIZE,
        sizeLabel: environment.webAppFileSize
      };
    }

    if (this.coreService.isIos) {
      return {
        maxAllowedSize: IOS_FILE_SIZE,
        sizeLabel: environment.iosFileSize
      };
    }

    return {
      maxAllowedSize: ANDROID_FILE_SIZE,
      sizeLabel: environment.androidFileSize
    };
  }

  /**
   * Resets upload-related UI state.
   */
  private resetUploadState(id: number): void {
    this.imageData = null;
    this.imageObj = null;
    this.showPhotoMandatoryAlwaysError = false;
    this.showPhotoMandatorySafetyError = false;
    this.viewIcon(id);
  }

  /**
   * Controls preview icon visibility.
   */
  viewIcon(id: number): void {
    this.showImage = this.inspectionItems()
      .some(item => item.veH_INSPECT_ITEM_ID === id && item.defect_Imedia != null);
  }

  /**
   * Emits final submission intent.
   */
  submitInspection() {
    this.isFormSubmitted = true;
    this.tempFormSubmitted = true;
    this.isTowingTrailerSubmitted = true;
    this.emitSubmission.emit();
  }

  /**
   * Handles explicit roadworthy selection.
   */
  changeIsRoadworthy(flag: boolean) {
    this.isRoadoworthySubmitted = true;
    this.isTowingTrailerSubmitted = false;
    this.isTowingTrailer = undefined;
    this.isRoadWorthy = flag;
  }

  changeIsTowingTrailer(flag: boolean) {
    this.isTowingTrailerSubmitted = true;
    this.isTowingTrailer = flag;
  }

  /**
   * Indicates whether any tickbox validation remains unresolved.
   */
  get isTickBoxValidation() {
    const tickBoxValidationsCount = this.inspectionItems().reduce(
      (acc, item) => acc + (
        !item.iS_SECTION_HEADER &&
          item.inpuT_TYPE == 'TICKBOX' &&
          (
            (!item.alloW_NA && item.isDefet == null) ||
            (item.alloW_NA && (item.isDefet == null || item.iS_NA == null))
          )
          ? 1 : 0
      ),
      0
    );

    return tickBoxValidationsCount > 0;
  }

  /**
   * Prompts confirmation before media removal.
   */
  async removeFile(inspectionTime: InspectionTypeItemsResponse) {
    const alert = await this.alertCtrl.create({
      message: this.translateStr("Remove_Image"),
      mode: 'md',
      buttons: [
        {
          text: this.translateStr("FUEL_LABEL5"),
          role: "cancel",
          cssClass: "bg-light-cancel",
          handler: () => { },
        },
        {
          text: this.translateStr("RD_MASTER_ES_LABEL13"),
          cssClass: 'btn_secondary text-white ps-4 pe-4',
          handler: () => {
            this.handleRemoveFile(inspectionTime);
          },
        },
      ],
    });

    await alert.present();
  }

  /**
   * Clears media from inspection item.
   */
  handleRemoveFile(inspectionItem: InspectionTypeItemsResponse) {
    inspectionItem.defect_Imedia = null;
    inspectionItem.defect_Imedia_Mime_Type = null;
    inspectionItem.isFileTypeValid = true;
    inspectionItem.isFileSizeValid = true;
    inspectionItem.invalidFileMessage = null;

    if (this.tempFormSubmitted && this.checkInspectionPhotoValidation(inspectionItem)) {
      inspectionItem.photO_MANDATORY_ANS = true;
    }
  }

  checkInspectionPhotoValidation(inspectionItem: InspectionTypeItemsResponse) {
    return (
      (
        inspectionItem.photO_MANDATORY === 'ALWAYS' &&
        inspectionItem.isDefet === true &&
        !inspectionItem.defect_Imedia
      ) ||
      (
        inspectionItem.photO_MANDATORY == 'SAFETY' &&
        this.isRoadWorthy == false &&
        inspectionItem.safetY_IMPACT_IND == 'Y' &&
        inspectionItem.safetY_IMPACT_IND_BOOL == true &&
        !inspectionItem.defect_Imedia
      ) ||
      (
        inspectionItem.commenT_MANDATORY === 'ALWAYS' &&
        inspectionItem.isDefet == true &&
        (!inspectionItem.comment || inspectionItem.comment.trim() === '')
      ) ||
      (
        inspectionItem.commenT_MANDATORY == 'SAFETY' &&
        inspectionItem.isDefet == true &&
        inspectionItem.safetY_IMPACT_IND_BOOL &&
        (!inspectionItem.comment || inspectionItem.comment.trim() == '')
      )
    )
  }

  /**
   * Opens media preview modal.
   */
  async showModal(item: InspectionTypeItemsResponse) {
    await this.coreService.openMediaModal({
      defecT_IMEDIA: item.defect_Imedia,
      defecT_IMEDIA_MIME_TYPE: item.defect_Imedia_Mime_Type
    });
  }

  get isTrailerInspection() {
    return this.route.snapshot.paramMap.get('isTrailerInspection') ? true : false;
  }

  async presentImgActionSheet(ev: any, id: number) {
    setTimeout(async () => {
      const actionSheetImg = await this.actionSheetCtrl.create({
        header: this.translateStr('SELECT_LABEL'),
        buttons: [
          {
            text: this.translateStr('CAM_LABEL'),
            cssClass: 'camera_button',
            icon: 'camera',
            handler: () => {
              this.openCamera(CameraSource.Camera, id);
            }
          },
          {
            text: this.translateStr('GAL_LABEL'),
            cssClass: 'gallery_button',
            icon: 'images',
            handler: () => {
              this.openCamera(CameraSource.Photos, id);
            }
          },
          {
            text: this.translateStr('FUEL_LABEL8'),
            role: 'cancel'
          }
        ]
      });

      await actionSheetImg.present();

    }, 100);
  }

  async openCamera(source: CameraSource, id: number) {
    try {
      this.imageData = await this.photoUploadService.capture(source, "defect", 'Asset image for inspection');
      if (!this.imageData?.data.base64Data) return;

      // Update inspection item
      this.inspectionItems()
        .filter(item => item.veH_INSPECT_ITEM_ID === id)
        .forEach(item => {
          item.defect_Imedia = this.imageData?.data.base64Data!;
          item.defect_Imedia_Mime_Type = this.imageData?.mimeType!;
          item.photO_MANDATORY_ANS = false
        });

      this.imageData = null;
      this.viewIcon(id);

    } catch (err) {
      console.error('Camera error', err);
      throw err;
    }
  }

  filterMobileData(base64Image: string) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = base64Image;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        let maxWidth = 2048;
        let maxHeight = 2048;
        let width = img.width;
        let height = img.height;

        // Calculate the new width and height to maintain aspect ratio
        if (width > height && width > maxWidth) {
          height *= maxWidth / width;
          width = maxWidth;
        }
        else if (height > maxHeight) {
          width *= maxHeight / height;
          height = maxHeight;
        }

        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;

        // Draw the image on the canvas
        ctx?.drawImage(img, 0, 0, width, height);

        // Get the compressed data URL
        const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.8); // 80% quality
        const returnStr = compressedDataUrl.substr(compressedDataUrl.indexOf(",") + 1);
        resolve(returnStr);
      };
      img.onerror = (error) => {
        reject(error);
      };
    });
  }


  resetVariables() {
    this.isFormSubmitted = false;
    this.tempFormSubmitted = false;
    this.isRoadoworthySubmitted = false;
    this.isTowingTrailerSubmitted = false;
    this.isValidMimetype = false;
    this.isFileSizeValid = false;
    this.invalidFileMessage = '';
    this.showPhotoMandatoryAlwaysError = false;
    this.showPhotoMandatorySafetyError = false;
    this.showImage = false;
    this.isRoadWorthy = undefined;
    this.isTowingTrailer = undefined;
    this.itemListForTemplate = [];
    this.topCommentError = '';
  }

  setisRoadworthySubmitted(value: boolean) {
    this.isRoadoworthySubmitted = value;
  }
}
